import { useDispatch } from 'react-redux';
import { FormApi, FORM_ERROR } from 'final-form';
import React, { useCallback, useState } from 'react';
import { useEffect } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { Form as FinalForm } from 'react-final-form';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { object, string } from 'yup';
import { MessageComposeFilters } from '../components/MessageComposeFilters';
import { FileUploadField, RequestsTypeaheadField, TextAreaField } from '.';
import { API_URL, ComposeMessageRequest, DocumentModel, useComposeMessage } from '../api';
import { PatientRequestViewModelFragment, ServiceRequestViewFragment } from '../graphql';
import { navigateToMessages, AppRootState, tenancyFeatureSelector } from '../redux';
import { FilePreview, GeneralObject, AnyType } from '../interfaces';
import {
  clientApplicationNameSelector,
  getFilePayload,
  requestStatusValueFormatter,
  validateFormValues
} from '../utils';
import { PatientSearchField } from './PatientSearch';
import { ValueFormatterParams } from 'ag-grid-community';

const messageSchema = object({
  message: string().max(5000).required().label('Message'),
  patientId: string().required().label('Patient Name'), // ID is internal - name is user-facing.
  serviceRequestId: string().required().label('Request ID'),
  messageTopic: string().max(100).required().label('Topic'),
  drug: string().max(100).required().label('Product')
});

const giSrMessageSchema = object({
  message: string().max(5000).required().label('Message'),
  drug: string().max(100).required().label('Product'),
  patientId: string().required().label('Patient Name'), // ID is internal - name is user-facing.
  messageTopic: string().max(100).required().label('Topic'),
  selfServiceRequest: string().required().label('Self-Service Request')
});

export const MessageCompose = () => {
  const dispatch = useDispatch();
  const { search: queryString } = useLocation();
  const currentUser = useSelector((state: AppRootState) => state.auth?.session?.user);
  const queryParams = new URLSearchParams(queryString);
  const patientId = queryParams.get('patient') ?? '';
  const requestId = queryParams.get('request') ?? '';
  const applicationName = useSelector(clientApplicationNameSelector);
  const [requestIdOptions, setRequestIdOptions] = useState<any[]>([]);
  const [isGISrNeeded, setIsGISrNeeded] = useState<boolean | null>(false);
  const [isRequestIdSelected, setRequestId] = useState<boolean | null>(false);
  const [whichRequest, setSelectedRequest] = useState<AnyType | null>(null);
  const [isPatientIdSelected, setPatientId] = useState<boolean | null>(false);
  const [eServicesOptions, setEServicesOptions] = useState<any[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<string>();
  const secureMessageClosedServiceEnabled = useSelector(
    (state: AppRootState) =>
      (((state.app?.entities?.features as AnyType) ?? {})['secure-message.closed-service-request'] as AnyType)?.data
        ?.enabled
  );
  const requestsFilterMapping: AnyType = useSelector<AnyType>(tenancyFeatureSelector('sr.filters_v1.5'));

  /**
   * true = request is complete
   * false = request is not complete
   * null = a request has not loaded to determine the status
   */
  const [isRequestComplete, setIsRequestComplete] = React.useState<boolean | null>(null);
  const [isHiding, setIsHiding] = React.useState<boolean>(false);

  const uploadIcon = (
    <>
      <i className='fa-solid fa-paperclip-vertical'></i> <span style={{ marginLeft: '3px' }}>Attachment</span>
    </>
  );

  useEffect(() => {
    if (isGISrNeeded) {
      setIsRequestComplete(!isGISrNeeded);
    }
  }, [isGISrNeeded]);

  const { mutate: sendMessage } = useComposeMessage({
    base: API_URL
  });

  const onSend = useCallback(
    async (values: GeneralObject, form: FormApi) => {
      const files = (values.files as Array<FilePreview>)?.map<DocumentModel | undefined>((f) =>
        getFilePayload(f)
      ) as DocumentModel[];

      const payload: ComposeMessageRequest = {
        applicationName: applicationName,
        files,
        messageText: String(values.message).trim(),
        patientId: Number(values.patientId),
        serviceRequestId: values.serviceRequestId !== '' ? Number(values.serviceRequestId) : null,
        subject: String(values.messageTopic).trim(), // Include messageTopic as the subject
        userName: currentUser?.Login,
        transactionId:
          values?.selfServiceRequest && !values?.serviceRequestId
            ? String(values?.selfServiceRequest)?.split(',')?.[0]
            : null,
        transactionType:
          values?.selfServiceRequest && !values?.serviceRequestId
            ? String(values?.selfServiceRequest)?.split(',')?.[1]
            : null,
        transactionDate:
          values?.selfServiceRequest && !values?.serviceRequestId
            ? String(values?.selfServiceRequest)?.split(',')?.[2]
            : null,
        sRDrugName: values?.drug ? String(values?.drug) : null,
        physicianNPI: values?.prescriberNPI ? String(values?.prescriberNPI) : null,
        providerId: values?.providerId ? Number(values?.providerId) : null
      };

      const result = await sendMessage(payload);
      if (result.errors && result.errors.length > 0) {
        return { [FORM_ERROR]: result.errors.map((e) => e.description).join(' ') };
      }
      // on sent
      dispatch(navigateToMessages());
      setTimeout(form.reset, 0);
    },
    [currentUser]
  );

  const onPatientSelect = (patient: PatientRequestViewModelFragment, form: FormApi) => {
    setIsGISrNeeded(false);
    form.change('patientId', patient?.patientId);
    form.change('providerId', patient?.physicianId);
    setPatientId(patient?.patientId ? true : false);
    setRequestId(!patient?.patientId ? false : null);

    // Clear the service request id unless the patient is the default request id. This covers scenarios where compose
    // is rendered by way of url parameters and there selected request loads before the selected patient.
    String(patient?.patientId) !== patientId && form.change('serviceRequestId', '');
  };

  const onRequestSelect = (form: FormApi, request?: any) => {
    if (isHiding) {
      setIsHiding(false);
      return;
    }

    if (request?.isEserviceTransaction) return;
    setRequestId(request?.id ? true : false);
    form.change('serviceRequestId', request?.id ?? '');
    setSelectedRequest(request ?? null);
    form.change('drug', request?.sRDrugName ?? '');
    setIsRequestComplete(secureMessageClosedServiceEnabled ? false : request?.requestStatus === 'C');
  };

  const isMessageTopicActive = isPatientIdSelected ? (isRequestIdSelected ? true : false) : false;

  const isTextAreaActive = isPatientIdSelected
    ? isRequestIdSelected
      ? selectedFilter
        ? true
        : false
      : false
    : false;

  const onFilterChange = (newFilter: string, form: FormApi) => {
    setSelectedFilter(newFilter);
    form.change('messageTopic', newFilter);
  };

  function setAlertVariant(statusText: any) {
    if (statusText != null) {
      if (statusText.includes('Action Required')) {
        return {
          background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.80) 0%, rgba(255, 255, 255, 0.80) 100%), #C82333',
          border: '1px solid #C82333',
          color: '#C82333',
          fontWeight: '700',
          fontFamily: 'Arial'
        };
      } else if (statusText.includes('Pending')) {
        return {
          background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%), #0084A9',
          border: '1px solid #0084A9',
          color: 'white',
          fontWeight: '700',
          fontFamily: 'Arial'
        };
      } else if (statusText.includes('Complete')) {
        return {
          background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), #218838',
          border: '1px solid #218838',
          color: '#218838',
          fontWeight: '700',
          fontFamily: 'Arial'
        };
      } else {
        return {
          background: 'white',
          border: '1px solid #ADB5BD',
          color: '#212529',
          fontWeight: '700',
          fontFamily: 'Arial'
        };
      }
    }
  }

  function setAlertIconVariant(requestType: any): string | undefined {
    if (requestType == null) {
      return 'fa-thin fa-folder-medical';
    }
    if (requestType.includes('Prior Authorization')) {
      return 'fa-thin fa-badge-check';
    } else if (requestType.includes('Request Complete') || requestType.includes('Request InComplete')) {
      return 'fa-thin fa-capsules';
    } else if (requestType.includes('Benefit Investigation')) {
      return 'fa-thin fa-user-doctor-hair-long';
    } else if (requestType.includes('Hub Enrollment')) {
      return 'fa-thin fa-folder-medical';
    } else {
      return 'fa-thin fa-folder-medical';
    }
  }

  const drugStyle: React.CSSProperties = {
    color: '#115099',
    fontFamily: 'Arial',
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: '700',
    position: 'absolute',
    bottom: '60px',
    textIndent: '-20px'
  };

  const patientHubStatus = requestStatusValueFormatter(
    { data: whichRequest } as ValueFormatterParams,
    requestsFilterMapping
  );

  const getStatus = (data: any) => {
    if (data.requestStatus === 'C') {
      return 'Request complete';
    } else if (data.requestType.includes('Patient Intake')) {
      return 'HUB Enrollment';
    } else {
      return data.requestType;
    }
  };

  return (
    <FinalForm
      onSubmit={onSend}
      validate={validateFormValues(isGISrNeeded ? giSrMessageSchema : messageSchema)}
      initialValues={messageSchema.default()}
      render={({ form, handleSubmit, submitting, submitError, hasValidationErrors, values, errors }) => (
        <Form onSubmit={handleSubmit} noValidate id='HCP-Message'>
          <Row>
            <Col className='px-5'>
              <Form.Group as={Row} className='align-items-center'>
                <Form.Label className='text-right d-flex align-items-center' column sm={2}>
                  <i className='fa-light fa-user-injured me-2'></i>
                  Select Patient:
                </Form.Label>
                <div style={{ width: '386px' }}>
                  <PatientSearchField
                    defaultPatientId={patientId}
                    fieldName='patientId'
                    placeholder='Patient Name or DOB'
                    onPatientSelect={(patient: PatientRequestViewModelFragment) => onPatientSelect(patient, form)}
                    npiInfoSearch='NPISearch'
                  />
                </div>
              </Form.Group>
              <Form.Group as={Row} className='align-items-center mt-4'>
                <Form.Label
                  className='text-right d-flex align-items-center'
                  style={!isPatientIdSelected ? { color: 'gray' } : {}}
                  column
                  sm={2}>
                  <i className='fa-light fa-cabinet-filing me-2'></i>
                  Select Request ID:
                </Form.Label>
                <div className={!isPatientIdSelected ? 'requestfield' : ''} style={{ width: '386px' }}>
                  <RequestsTypeaheadField
                    defaultRequestId={requestId}
                    fieldName='serviceRequestId'
                    patientId={values.patientId}
                    placeholder='Request ID'
                    onRequestSelect={(request?: ServiceRequestViewFragment) => onRequestSelect(form, request)}
                    requestIdOptions={requestIdOptions}
                    setRequestIdOptions={setRequestIdOptions}
                    setEServicesOptions={setEServicesOptions}
                    setIsGISrNeeded={setIsGISrNeeded}
                    isGISrNeeded={isGISrNeeded}
                    npiInfoSearch='NPISearch'
                  />
                </div>

                {whichRequest && whichRequest.statusText && (
                  <Alert
                    className='d-flex align-items-center ms-auto mb-0'
                    style={{
                      width: 'auto',
                      padding: '3px 40px 4px 25px',
                      marginRight: '2rem',
                      borderRadius: '0',
                      ...setAlertVariant(patientHubStatus)
                    }}>
                    <i className={setAlertIconVariant(whichRequest?.requestType)} style={{ fontSize: '36px' }}></i>
                    <div className='align-items-center ms-2'>
                      <p className='mb-0' style={drugStyle}>
                        {whichRequest?.sRDrugName || 'No drug'}
                      </p>
                      <p className='mb-0' style={{ fontSize: '16px' }}>
                        {getStatus(whichRequest)}
                      </p>
                      <p className='mb-0' style={{ fontSize: '14px', fontWeight: '400' }}>
                        {patientHubStatus || 'Not Available'}
                      </p>
                    </div>
                  </Alert>
                )}
              </Form.Group>
              <Form.Group as={Row} className='align-items-center mt-4'>
                <Form.Label
                  className={`text-right d-flex align-items-center`}
                  name='messageTopic'
                  column
                  style={!isMessageTopicActive ? { color: 'gray' } : {}}
                  sm={2}>
                  <i className='fa-light fa-message-lines me-2'></i>
                  Message Topic:
                </Form.Label>
                <Col>
                  <div className='me-3' aria-disabled={!isRequestIdSelected}>
                    <MessageComposeFilters
                      disabled={!isMessageTopicActive}
                      onFilterChange={(newFilter) => onFilterChange(newFilter, form)}
                    />
                  </div>
                </Col>
              </Form.Group>
              <div className='border p-3 mt-4'>
                <Form.Group as={Row} className='align-items-center'>
                  <Col>
                    <TextAreaField
                      name='message'
                      placeholder='Select a topic and enter your message here.'
                      disabled={!isTextAreaActive}
                      style={{ opacity: isTextAreaActive ? '' : '0.65' }}
                      maxLength={5000}
                      rows={5}
                      className='w-100'
                    />
                  </Col>
                </Form.Group>
                {submitError && (
                  <Form.Group>
                    <span className='text-danger'>{submitError}</span>
                  </Form.Group>
                )}
                <Form.Group as={Row} className='align-items-center mt-4'>
                  <Col>
                    <Button
                      type='submit'
                      variant='secondary-new'
                      id='Message-Submit'
                      size='sm'
                      disabled={submitting || hasValidationErrors}
                      data-testid='cmx__modal-send-button'>
                      <i className='fa-thin fa-paper-plane' style={{ marginRight: '6px' }}></i>
                      Send
                    </Button>
                    {submitting || hasValidationErrors ? (
                      <Button
                        variant='secondary-new'
                        className='ms-3'
                        id='Message-Attach'
                        size='sm'
                        disabled={true}
                        data-testid='cmx__modal-attach-button'>
                        <i className='fa-solid fa-paperclip-vertical' style={{ marginRight: '6px' }}></i>
                        Attachment
                      </Button>
                    ) : (
                      <FileUploadField
                        disabled={submitting || hasValidationErrors}
                        icon={uploadIcon}
                        name='files'
                        dropzoneOptions={{ maxFiles: 5 }}
                      />
                    )}
                  </Col>
                </Form.Group>
              </div>
            </Col>
          </Row>
        </Form>
      )}
    />
  );
};
